import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {
  GetAdvancedParams,
  GetParams,
  GetResponse,
  PostResponse,
  Service,
  ServiceCreate,
  ServiceGood,
  ServiceGoodCreate,
  ServiceOrder,
  ServiceOrderCreate,
  ServiceOrderMinimal,
  ServiceOrderTemplate,
  ServiceOrderTemplateCreate,
  ServiceOrderWithTemplateParams,
  ServiceType,
  TypeAnalyticService,
  UpdateMultipleServiceOrderState,
  UpdateServiceOrderWithTemplateParams,
  BatchAssignEquipmentPayload,
  BatchAssignDriverPayload,
  BatchAssignResult,
  BatchAssignTransportDetailPayload
} from '@app/shared';
import {CompanyService, convertGetParamsToHttpParams, setGetParamsById} from '@app/core';

@Injectable({
  providedIn: 'root'
})
export class StServiceService {

  setGetParamsById = setGetParamsById;

  /** URLs principales relacionadas con Locations y Transports **/
  serviceUrl = environment.coreApiBaseUrl + '/api/Service';
  serviceGoodUrl = environment.coreApiBaseUrl + '/api/serviceGood';
  serviceOrderUrl = environment.coreApiBaseUrl + '/api/ServiceOrder';
  serviceOrderAdvancedUrl = environment.coreApiBaseUrl + '/api/ServiceOrder/get/advanced';
  serviceOrderAdvancedForADminUrl = environment.coreApiBaseUrl + '/api/Administration/get/service-order/advanced-for-admin';
  serviceOrderTemplateUrl = environment.coreApiBaseUrl + '/api/serviceOrderTemplate';
  serviceTypeUrl = environment.coreApiBaseUrl + '/api/service-type';
  typeAnalyticServiceUrl = environment.coreApiBaseUrl + '/api/type-analytic-service';
  containerTypeUrl = environment.coreApiBaseUrl + '/api/ContainerType';
  servicePricingUrl = environment.coreApiBaseUrl + '/api/service-pricing';

  constructor(private http: HttpClient, private companyService: CompanyService) {
  }

  /** Metodos de obtener/crear/actualizar/eliminar Service **/
  getService(getParams: GetParams): Observable<GetResponse> {
    const params = convertGetParamsToHttpParams({...getParams, companyId: this.companyService.selectedCompanyId$.value});
    const headers = this.companyService.getCompanyHeaders();

    return this.http.get<GetResponse>(this.serviceUrl, {
      params,
      headers
    });
  }

  getServiceById(serviceId: number): Observable<GetResponse> {
    const headers = this.companyService.getCompanyHeaders();
    const params: GetParams = {
      startRow: 0,
      endRow: 100,
      sortDirection: 'asc',
      sortProperty: 'id',
      filterProperty: 'id',
      filterValue: serviceId.toString(),
    }

    return this.http.get<GetResponse>(
      this.serviceUrl,
      {params: convertGetParamsToHttpParams({...params, companyId: this.companyService.selectedCompanyId$.value}),
        headers});
  }

  createService(newService: ServiceCreate): Observable<PostResponse> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<PostResponse>(
      this.serviceUrl,
      {...newService, companyId: this.companyService.selectedCompanyId$.value},
      {headers});
  }

  updateService(service: Service): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.put(this.serviceUrl, service, {headers});
  }

  patchService(payload: any): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    // Solo agregar companyId si el payload no lo tiene ya
    // const finalPayload = payload.hasOwnProperty('companyId')
    //   ? payload
    //   : {...payload, companyId: this.companyService.selectedCompanyId$.value};
    // console.log(finalPayload);
    return this.http.patch(this.serviceUrl, payload, {headers});
  }

  deleteService(serviceIds: number[]): Observable<any> {
    const headers = this.companyService.getCompanyHeaders();
    const params = { ids: serviceIds.map(id => id.toString()) };

    return this.http.delete(this.serviceUrl, {
      headers,
      params: new HttpParams({ fromObject: params })
    });
  }

  /** Metodos de obtener/crear/actualizar/eliminar ServiceGood **/
  getServiceGood(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.serviceGoodUrl, {params: convertGetParamsToHttpParams(getParams)});
  }

  createServiceGood(newServiceGood: ServiceGoodCreate): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.serviceGoodUrl, newServiceGood);
  }

  updateServiceGood(serviceGood: ServiceGood): Observable<any> {
    return this.http.put(this.serviceGoodUrl, serviceGood);
  }

  deleteServiceGood(serviceGoodIds: number[]): Observable<any> {
    return this.http.delete(this.serviceGoodUrl, {body: {ids: serviceGoodIds}});
  }

  /** Metodos de obtener/crear/actualizar/eliminar ServiceOrder **/
  getServiceOrder(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.serviceOrderUrl, {params: convertGetParamsToHttpParams(getParams)});
  }

  getAdvancedServiceOrder(getParams: GetAdvancedParams): any {
    return this.http.post<GetResponse>(this.serviceOrderAdvancedUrl, getParams);
  }

  /** Todas las SO del servicio con carga mínima (id, reference, state) para totales, filtros y navegación en drawer */
  getServiceOrdersByServiceId(serviceId: number): Observable<{ data: ServiceOrderMinimal[] }> {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.get<{ data: ServiceOrderMinimal[] }>(
      `${this.serviceOrderUrl}/by-service/${serviceId}`,
      { headers }
    );
  }

  createServiceOrder(newServiceOrder: ServiceOrderCreate): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.serviceOrderUrl, newServiceOrder);
  }

  updateServiceOrder(serviceOrder: any): Observable<any> {
    return this.http.put(this.serviceOrderUrl, serviceOrder);
  }

  patchServiceOrder(payload: any): Observable<any> {
    return this.http.patch(this.serviceOrderUrl, payload);
  }

  deleteServiceOrder(serviceOrderIds: number[]): Observable<any> {
    return this.http.delete(this.serviceOrderUrl, {body: {ids: serviceOrderIds}});
  }

  getAdvancedServiceOrderForAdmin(getParams: GetAdvancedParams): any {
    const headers = this.companyService.getCompanyHeaders();
    return this.http.post<GetResponse>(this.serviceOrderAdvancedForADminUrl, getParams, {headers});
  }

  /** Metodos de obtener/crear/actualizar/eliminar ServiceType **/
  getServiceType(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.serviceTypeUrl, {params: convertGetParamsToHttpParams(getParams)});
  }

  createServiceType(newServiceType: { isActive: boolean, name: string }): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.serviceTypeUrl, newServiceType);
  }

  updateServiceType(serviceType: ServiceType): Observable<any> {
    return this.http.put(this.serviceTypeUrl, serviceType);
  }

  deleteServiceType(serviceTypeIds: number[]): Observable<any> {
    return this.http.delete(this.serviceTypeUrl, {body: {ids: serviceTypeIds}});
  }

  /** Metodos de obtener/crear/actualizar/eliminar TypeAnalyticService **/
  getTypeAnalyticService(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.typeAnalyticServiceUrl, {params: convertGetParamsToHttpParams(getParams)});
  }

  createTypeAnalyticService(newTypeAnalyticService: { isActive: boolean, name: string }): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.typeAnalyticServiceUrl, newTypeAnalyticService);
  }

  updateTypeAnalyticService(typeAnalyticService: TypeAnalyticService): Observable<any> {
    return this.http.put(this.typeAnalyticServiceUrl, typeAnalyticService);
  }

  deleteTypeAnalyticService(typeAnalyticServiceIds: number[]): Observable<any> {
    return this.http.delete(this.typeAnalyticServiceUrl, {body: {ids: typeAnalyticServiceIds}});
  }

  getContainerTypes(): Observable<GetResponse> {
    const params: GetParams = {
      startRow: 0,
      endRow: 200,
      sortDirection: 'asc',
      sortProperty: 'id'
    }
    return this.http.get<GetResponse>(this.containerTypeUrl, {params: convertGetParamsToHttpParams(params)})
  }

  /** Metodos de obtener/crear/actualizar/eliminar ServiceOrderTemplate **/
  getServiceOrderTemplate(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.serviceOrderTemplateUrl, {params: convertGetParamsToHttpParams(getParams)});
  }

  createServiceOrderTemplate(newServiceOrderTemplate: ServiceOrderTemplateCreate): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.serviceOrderTemplateUrl, newServiceOrderTemplate);
  }

  updateServiceOrderTemplate(ServiceOrderTemplate: ServiceOrderTemplate): Observable<any> {
    return this.http.put(this.serviceOrderTemplateUrl, ServiceOrderTemplate);
  }

  patchServiceOrderTemplate(payload: any): Observable<any> {
    return this.http.patch(this.serviceOrderTemplateUrl, payload);
  }

  deleteServiceOrderTemplate(ServiceOrderTemplatesIds: number[]): Observable<any> {
    return this.http.delete(this.serviceOrderTemplateUrl, {body: {ids: ServiceOrderTemplatesIds}});
  }

  createServiceOrderWithTemplate(params: ServiceOrderWithTemplateParams): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.serviceOrderTemplateUrl + '/replicate', params);
  }

  updateServiceOrdersWithTemplate(params: UpdateServiceOrderWithTemplateParams): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.serviceOrderTemplateUrl + '/propagate', params);
  }

  patchSevicePricing(payload: any): Observable<any> {
    return this.http.patch(this.servicePricingUrl, payload);
  }

  patchMultipleServiceOrderState(payload: UpdateMultipleServiceOrderState): Observable<any> {
    return this.http.patch(environment.coreApiBaseUrl + '/api/Event/batch', payload);
  }

  /**
   * Asigna un equipo a múltiples OS de forma masiva.
   * Requiere endpoint backend: PATCH /api/ServiceOrder/batch-assign-equipment
   */
  patchBatchAssignEquipment(payload: BatchAssignEquipmentPayload): Observable<BatchAssignResult> {
    return this.http.patch<BatchAssignResult>(
      `${this.serviceOrderUrl}/batch-assign-equipment`, payload
    );
  }

  /**
   * Asigna un conductor a múltiples OS de forma masiva.
   * Requiere endpoint backend: PATCH /api/ServiceOrder/batch-assign-driver
   */
  patchBatchAssignDriver(payload: BatchAssignDriverPayload): Observable<BatchAssignResult> {
    return this.http.patch<BatchAssignResult>(
      `${this.serviceOrderUrl}/batch-assign-driver`, payload
    );
  }

  /**
   * Asigna conductor, camion, rampla y fecha tentativa por OS.
   * Requiere endpoint backend: PATCH /api/ServiceOrder/batch-assign-transport-detail
   */
  patchBatchAssignTransportDetail(payload: BatchAssignTransportDetailPayload): Observable<BatchAssignResult> {
    return this.http.patch<BatchAssignResult>(
      `${this.serviceOrderUrl}/batch-assign-transport-detail`, payload
    );
  }
}
